import { FC, useContext, useMemo, useState } from 'react';
import moment from 'moment';

import { i18n as I18n } from 'utils/i18n';
import { dateFormatForSearchUI } from 'utils/date';
import { SearchContext } from '../../state/search';
import FilterDropdown from '../filterDropdown';
import IncrementalInput from '../incrementalInput';
import DatepickerField from '../fields/datepickerField';
import CheckBoxList from '../fields/checkBoxList';
import Switch from '../switch';

// constants
import { distances } from '../../constants/distances';
import { CCG_BOOKING_RESTRICTION } from '../../constants/bookings';

type View = 'list' | 'calendar';

interface Props {
  changeViewType?: (view: View) => void;
  updateCurrentPage?: (pageNumber: number) => void;
  currentViewType?: View;
  aggregates?: any;
  showPropertyFilter?: boolean;
}

const defaultAggregates = { property: [], distance: [] };

const toggle = value => previous =>
  previous.includes(value)
    ? previous.filter(item => item !== value)
    : [...previous, value];

const distanceLabel = value =>
  Object.keys(distances).find(key => distances[key] === value) ?? 'Distance';

const SpaceFilters: FC<Props> = ({
  changeViewType,
  currentViewType = 'list',
  updateCurrentPage = () => {},
  aggregates = defaultAggregates,
  showPropertyFilter = true,
  children,
}) => {
  const { filters, actions } = useContext(SearchContext);

  const [distance, setDistance] = useState(filters.distance);
  const [capacity, setCapacity] = useState(filters.capacity);
  const [properties, setProperty] = useState(filters.property);
  const [facets, setFacets] = useState(filters.facets);

  const showDistanceFilter = filters.location || filters.latLng;

  const maxDate = useMemo(
    () => moment().add(CCG_BOOKING_RESTRICTION, 'months').toDate(),
    []
  );

  const clearSelectedCheckboxes = () => {
    setDistance(null);
    setCapacity(null);
    setProperty([]);
    setFacets([]);
  };

  return (
    <div>
      {changeViewType && (
        <div className="space-filters__switch">
          <Switch
            label="Calendar View"
            toggled={currentViewType === 'calendar'}
            onSwitchChange={changeViewType}
          />
        </div>
      )}
      <div
        className="space-filters"
        data-cy="spaceFilters"
        data-testid="spaceFilters"
      >
        <div className="space-filters__container">
          <DatepickerField
            value={filters.date}
            onChange={date => {
              updateCurrentPage(1);
              actions.updateDate(date);
            }}
            placeholder="Date"
            dateFormat={dateFormatForSearchUI}
            dataCy="spaceFiltersDatePicker"
            maxDate={maxDate}
            isClearable
          />
          {showPropertyFilter && (
            <FilterDropdown
              label="Property"
              hideSelectAllButton
              handleApply={() => {
                updateCurrentPage(1);
                actions.updateProperty(properties);
              }}
              handleClearAll={() => {
                setProperty([]);
                updateCurrentPage(1);
                actions.updateProperty([]);
              }}
              selectedItems={filters.property}
            >
              <CheckBoxList
                id="property"
                name="property[]"
                options={aggregates.property.map(i => {
                  return {
                    name: `${i.name} (${i.count})`,
                    value: i.name,
                    checked: properties.includes(i.name),
                  };
                })}
                onChange={({ currentTarget: { value } }) => {
                  updateCurrentPage(1);
                  setProperty(toggle(value));
                }}
              />
            </FilterDropdown>
          )}

          {showDistanceFilter && (
            <FilterDropdown
              hideSelectAllButton
              label={distanceLabel(filters.distance)}
              handleApply={() => {
                updateCurrentPage(1);
                actions.updateDistance(distance);
              }}
              handleClearAll={() => {
                setDistance(null);
                updateCurrentPage(1);
                actions.updateDistance(null);
              }}
            >
              <CheckBoxList
                id="distance"
                name="distance"
                type="radio"
                className="field--radio-list"
                options={Object.keys(distances).map(key => ({
                  name: key,
                  value: distances[key],
                  checked: distances[key] === distance,
                }))}
                onChange={({ target: { value } }) => setDistance(Number(value))}
              />
            </FilterDropdown>
          )}

          <FilterDropdown
            hideSelectAllButton
            label={
              filters.capacity === null
                ? 'Capacity'
                : `Guests: ${filters.capacity}`
            }
            clearAllText={I18n.t('reset')}
            canApply={capacity && capacity >= 1}
            handleApply={() => {
              setCapacity(capacity);
              updateCurrentPage(1);
              actions.updateCapacity(capacity);
            }}
            handleClearAll={() => {
              setCapacity(null);
              updateCurrentPage(1);
              actions.updateCapacity(null);
            }}
          >
            <IncrementalInput
              id="capacity"
              label="Guests:"
              initialValue={filters.capacity === null ? 0 : filters.capacity}
              minValue={1}
              onChange={setCapacity}
            />
          </FilterDropdown>

          {aggregates.facets && (
            <FilterDropdown
              hideSelectAllButton
              label="Features"
              handleApply={() => {
                setFacets(facets);
                updateCurrentPage(1);
                actions.updateFacets(facets);
              }}
              handleClearAll={() => {
                setFacets([]);
                updateCurrentPage(1);
                actions.updateFacets([]);
              }}
              selectedItems={filters.facets}
            >
              <CheckBoxList
                id="facet"
                name="facets[]"
                options={aggregates.facets.map(i => {
                  return {
                    name: `${i.name} (${i.count})`,
                    value: i.name,
                    checked: facets.includes(i.name),
                  };
                })}
                onChange={({ currentTarget: { value } }) =>
                  setFacets(toggle(value))
                }
              />
            </FilterDropdown>
          )}
        </div>
        {children}
        <button
          className="button button--text"
          onClick={() => {
            updateCurrentPage(1);
            actions.clearAllFilters();
            clearSelectedCheckboxes();
          }}
        >
          {I18n.t('clear_all')}
        </button>
      </div>
    </div>
  );
};

export default SpaceFilters;
